import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import apiService from '../../services/ApiService';
import { useCurrentUser } from '../../shared/hooks/queries/useCurrentUser';
import { parseServerTimestamp } from '../../utils/dateFormat';
import { AGENT_DATA_CHANGED_EVENT, type AgentDataChange } from '../../hooks/useAgentChat';

interface PortfolioGroup {
    id: number;
    name: string;
    description?: string;
    owner_id: number;
    currency: string;
    discount_rate: number;
    asset_count: number;
    is_any_stale: boolean;
    created_at?: string;
    updated_at?: string;
}

interface PortfolioGroupFull extends PortfolioGroup {
    assets: any[];
    tags: any[];
    sharing: any[];
    kpis: any;
    computed_at?: string;
}

interface PortfolioContextType {
    groups: PortfolioGroup[];
    selectedGroup: PortfolioGroupFull | null;
    loading: boolean;
    computing: boolean;
    isOwner: boolean;
    canRead: boolean;
    canWrite: boolean;
    canShare: boolean;
    pendingRecomputeCount: number;
    refreshGroups: () => Promise<void>;
    selectGroup: (groupId: number) => Promise<void>;
    deselectGroup: () => void;
    computeGroup: () => Promise<void>;
}

const PortfolioContext = createContext<PortfolioContextType | null>(null);

export function usePortfolioContext() {
    const ctx = useContext(PortfolioContext);
    if (!ctx) throw new Error('usePortfolioContext must be used within PortfolioProvider');
    return ctx;
}

export function PortfolioProvider({ children }: { children: React.ReactNode }) {
    const [groups, setGroups] = useState<PortfolioGroup[]>([]);
    const [selectedGroup, setSelectedGroup] = useState<PortfolioGroupFull | null>(null);
    const [loading, setLoading] = useState(false);
    const [computing, setComputing] = useState(false);
    const { data: currentUser } = useCurrentUser();

    const { isOwner, canRead, canWrite, canShare } = useMemo(() => {
        if (!selectedGroup || !currentUser) {
            return { isOwner: false, canRead: false, canWrite: false, canShare: false };
        }
        if (selectedGroup.owner_id === currentUser.id) {
            return { isOwner: true, canRead: true, canWrite: true, canShare: true };
        }
        const myEntry = (selectedGroup.sharing || []).find((s: any) => s.user_id === currentUser.id);
        return {
            isOwner: false,
            canRead: !!myEntry?.right_to_read,
            canWrite: !!myEntry?.right_to_write,
            canShare: !!myEntry?.right_to_share,
        };
    }, [selectedGroup, currentUser]);

    const refreshGroups = useCallback(async () => {
        setLoading(true);
        try {
            const response = await apiService.getPortfolioGroups();
            if (response && response.ok) {
                const data = await response.json();
                setGroups(data);
            }
        } finally {
            setLoading(false);
        }
    }, []);

    const selectGroup = useCallback(async (groupId: number) => {
        setLoading(true);
        try {
            const response = await apiService.getPortfolioGroup(groupId);
            if (response && response.ok) {
                const data = await response.json();
                setSelectedGroup(data);
            }
        } finally {
            setLoading(false);
        }
    }, []);

    const deselectGroup = useCallback(() => {
        setSelectedGroup(null);
    }, []);

    const computeGroup = useCallback(async () => {
        if (!selectedGroup) return;
        setComputing(true);
        try {
            const response = await apiService.computePortfolio(selectedGroup.id);
            if (response && response.ok) {
                // Refresh the group (KPIs + asset is_stale flags) AND the groups list so the
                // sidebar's `is_any_stale` indicator clears.
                await Promise.all([selectGroup(selectedGroup.id), refreshGroups()]);
            }
        } finally {
            setComputing(false);
        }
    }, [selectedGroup, selectGroup, refreshGroups]);

    // Count of assets approved (or added) after the last group compute — derived from backend data,
    // so the warning persists across reloads. We exclude `is_stale` assets (those have their own banner)
    // and skip groups that have never been computed (initial state, no point flagging).
    const pendingRecomputeCount = useMemo(() => {
        if (!selectedGroup) return 0;
        const groupComputedAt = parseServerTimestamp(selectedGroup.computed_at);
        if (!groupComputedAt) return 0;
        const computedAtMs = groupComputedAt.getTime();
        return selectedGroup.assets.filter((a: any) => {
            if (a.is_stale) return false;
            const snapshot = parseServerTimestamp(a.snapshot_scenario_computed_at);
            if (!snapshot) return false;
            return snapshot.getTime() > computedAtMs;
        }).length;
    }, [selectedGroup]);

    useEffect(() => {
        refreshGroups();
    }, [refreshGroups]);

    // Keep latest selectedGroup id in a ref so the agent listener doesn't need
    // to re-bind every time the selection changes.
    const selectedGroupIdRef = useRef<number | null>(null);
    useEffect(() => {
        selectedGroupIdRef.current = selectedGroup?.id ?? null;
    }, [selectedGroup]);

    // Listen for agent write tools that mutated portfolios and refresh accordingly.
    useEffect(() => {
        const handler = (e: Event) => {
            const change = (e as CustomEvent<AgentDataChange>).detail;
            if (!change?.type?.startsWith?.('portfolio_')) return;

            const changedId = typeof change.portfolio_id === 'number' ? change.portfolio_id : null;
            const isSelected = changedId !== null && selectedGroupIdRef.current === changedId;

            // Always refresh the group list — covers create/delete/asset_count/is_any_stale changes.
            refreshGroups();

            // If the impacted portfolio is currently open, also reload its detail view
            // (KPIs, assets, tags, sharing, computed_at).
            if (isSelected && changedId !== null) {
                selectGroup(changedId);
            }
        };

        window.addEventListener(AGENT_DATA_CHANGED_EVENT, handler);
        return () => window.removeEventListener(AGENT_DATA_CHANGED_EVENT, handler);
    }, [refreshGroups, selectGroup]);

    return (
        <PortfolioContext.Provider value={{
            groups, selectedGroup, loading, computing,
            isOwner, canRead, canWrite, canShare,
            pendingRecomputeCount,
            refreshGroups, selectGroup, deselectGroup, computeGroup,
        }}>
            {children}
        </PortfolioContext.Provider>
    );
}
